import { STROLLER_DETAIL_ADDED_TO_PROFILE, STROLLER_DETAIL_REMOVED_FROM_PROFILE } from 'mk/autogenerated/translations/StrollersDetail.sagas.0c95bc0d13c2e3f832267cbc5d6535aa'
import {
    strollers_api_v2_stroller_delete_url,
    strollers_api_v2_stroller_detail_url,
    strollers_api_v2_stroller_detail_variant_url,
    strollers_api_v2_stroller_load_more_similars_url,
    strollers_api_v2_user_strollers_add_user_stroller_url,
    strollers_api_v2_user_strollers_remove_user_stroller_url,
} from 'mk/urls/functions';
import { blogsProfileUrl } from 'mk2/apps/blogs/urls';
import {
    strollersDetailAddToProfileApi,
    strollersDetailChangeVariantFeatureValueApi,
    strollersDetailDeleteStrollerApi,
    strollersDetailFetch,
    strollersDetailFetchTrigger,
    strollersDetailLoadMoreSimilarsApi,
    strollersDetailRemoveFromProfileApi,
    StrollersDetailAddToProfileAction,
    StrollersDetailChangeVariantFeatureValueTriggerAction,
    StrollersDetailFetchNormalizedResponse,
    StrollersDetailFetchTriggerAction,
    StrollersDetailLoadMoreSimilarsNormalizedResponse,
    StrollersDetailLoadMoreSimilarsTriggerAction,
    STROLLERS_DETAIL_ADD_TO_PROFILE,
    STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER,
    STROLLERS_DETAIL_DELETE_STROLLER_TRIGGER,
    STROLLERS_DETAIL_FETCH_TRIGGER,
    STROLLERS_DETAIL_LOAD_MORE_SIMILARS_TRIGGER,
    STROLLERS_DETAIL_REMOVE_FROM_PROFILE,
} from 'mk2/apps/strollers/containers/StrollersDetail/StrollersDetail.actions';
import { Interpolate } from 'mk2/components/Interpolate';
import { Link } from 'mk2/components/Link';
import { handleXHRGetErrorSaga, handleXHRPostErrorSaga, normalizeError, XHRAction, XHRGetError, XHRPostError } from 'mk2/helpers/api';
import { showSuccessToast } from 'mk2/helpers/toasts';
import { url } from 'mk2/helpers/urls';
import { getLogger } from 'mk2/logger';
import React from 'react';
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';

const logger = getLogger('strollers/StrollersDetail.sagas');

interface FetchApiResponse {
    body: StrollersDetailFetchNormalizedResponse;
}

function* fetchStrollerSaga({ slug, variantId, xhr}: StrollersDetailFetchTriggerAction & XHRAction) {
    yield put(!!variantId ? strollersDetailFetch.request(slug, variantId) : strollersDetailFetch.request(slug));

    try {

        const response: FetchApiResponse = !variantId ? yield call(
            () => xhr.get(strollers_api_v2_stroller_detail_url(slug),
        )) : yield call(
            () => xhr.get(strollers_api_v2_stroller_detail_variant_url(slug, variantId),
        ));

        yield put(!!variantId ? strollersDetailFetch.success(slug, response.body, variantId) : strollersDetailFetch.success(slug, response.body));
    } catch (error) {
        yield handleXHRGetErrorSaga(error as XHRGetError, logger);
        yield put(!!variantId ? strollersDetailFetch.failure(slug, normalizeError(error), variantId) : strollersDetailFetch.failure(slug, normalizeError(error)));

        if (error.status === 404 && error.response && error.response.body?.location) {
            yield put(strollersDetailFetchTrigger(slug));
        }
    }
}

interface FetchMoreSimilarsApiResponse {
    body: StrollersDetailLoadMoreSimilarsNormalizedResponse;
}

function* fetchMoreSimilars({ slug, offset, xhr }: StrollersDetailLoadMoreSimilarsTriggerAction & XHRAction) {
    yield put(strollersDetailLoadMoreSimilarsApi.request(slug, offset));

    try {
        const response: FetchMoreSimilarsApiResponse = yield call(() => xhr.get(
            strollers_api_v2_stroller_load_more_similars_url(slug), { offset },
        ));
        yield put(strollersDetailLoadMoreSimilarsApi.success(slug, offset, response.body));
    } catch (error) {
        logger.error(error);
        yield put(strollersDetailLoadMoreSimilarsApi.failure(slug, offset, normalizeError(error)));
    }
}

function* addToProfile({ slug, strollerId, username, xhr }: StrollersDetailAddToProfileAction & XHRAction) {
    yield put(strollersDetailAddToProfileApi.request(slug, strollerId, username));

    try {
        const response: FetchApiResponse = yield call(() =>
            xhr.post(strollers_api_v2_user_strollers_add_user_stroller_url(), {
                strollerId,
                username,
            }),
        );
        yield put(
            strollersDetailAddToProfileApi.success(slug, strollerId, username, {
                entities: response.body.entities,
                result: response.body.result,
            }),
        );
        yield showSuccessToast(
            <Interpolate
                i18nKey={STROLLER_DETAIL_ADDED_TO_PROFILE}
                components={[<Link to={url(blogsProfileUrl, { username })} key={0} />]}
            />,
        );
    } catch (error) {
        logger.error(error);
        yield put(strollersDetailAddToProfileApi.failure(slug, strollerId, username, normalizeError(error)));
    }
}

function* removeFromProfile({ slug, strollerId, username, xhr }: StrollersDetailAddToProfileAction & XHRAction) {
    yield put(strollersDetailRemoveFromProfileApi.request(slug, strollerId, username));

    try {
        yield call(() =>
            xhr.post(strollers_api_v2_user_strollers_remove_user_stroller_url(), {
                strollerId,
                username,
            }),
        );
        yield put(
            strollersDetailRemoveFromProfileApi.success(slug, strollerId, username, { entities: {}, result: [] }),
        );
        yield showSuccessToast(
            <Interpolate
                i18nKey={STROLLER_DETAIL_REMOVED_FROM_PROFILE}
                components={[<Link to={url(blogsProfileUrl, { username })} key={0} />]}
            />,
        );
    } catch (error) {
        logger.error(error);
        yield put(strollersDetailRemoveFromProfileApi.failure(slug, strollerId, username, normalizeError(error)));
    }
}

interface DeleteStrollerApiResponse {
    body: {
        entities: StrollersDetailFetchNormalizedResponse['entities'];
        result: StrollersDetailFetchNormalizedResponse['result'];
    };
}

function* deleteStroller({ strollerId, xhr }: StrollersDetailAddToProfileAction & XHRAction) {
    if (!window.confirm('Are you sure you want to delete the stroller?')) {
        return;
    }

    yield put(strollersDetailDeleteStrollerApi.request(strollerId));

    try {
        const response: DeleteStrollerApiResponse = yield call(
            () => xhr.post(strollers_api_v2_stroller_delete_url(), { strollerId }),
        );

        yield put(strollersDetailDeleteStrollerApi.success(strollerId, response.body));
        yield showSuccessToast('Succesfully deleted stroller');
    } catch (error) {
        yield handleXHRPostErrorSaga(error as XHRPostError, logger);
        yield put(strollersDetailDeleteStrollerApi.failure(strollerId, normalizeError(error)));
    }
}

function* strollerVariant({ slug, linkedId, xhr }: StrollersDetailChangeVariantFeatureValueTriggerAction & XHRAction) {

    yield put(strollersDetailChangeVariantFeatureValueApi.request(linkedId));
}

export default function* root() {
    yield all([
        takeLatest(STROLLERS_DETAIL_FETCH_TRIGGER, fetchStrollerSaga),
        takeLatest(STROLLERS_DETAIL_LOAD_MORE_SIMILARS_TRIGGER, fetchMoreSimilars),
        takeEvery(STROLLERS_DETAIL_DELETE_STROLLER_TRIGGER, deleteStroller),
        takeEvery(STROLLERS_DETAIL_ADD_TO_PROFILE, addToProfile),
        takeEvery(STROLLERS_DETAIL_REMOVE_FROM_PROFILE, removeFromProfile),
        takeLatest(STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER, strollerVariant),
    ]);
}
